import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { formatDateTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Skeleton, useToast } from '../../../ds/components';
import { useMediaQuery } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { useCompleteTask } from '../../tasks/api';
import {
  exportHref,
  reviewKey,
  useReview,
  useReviewActions,
  type MeetingReview,
  type MeetingTask,
  type TranscriptLine,
} from '../api';
import { shortPersonName } from '../clock';
import { Player, type PlayerHandle } from './Player';
import styles from './ReviewPage.module.css';

/** Lines of the transcript card; „Volltext öffnen" shows all of them. */
const EXCERPT_LINES = 8;

function excerpt(lines: TranscriptLine[]): TranscriptLine[] {
  if (lines.length <= EXCERPT_LINES) return lines;
  const firstMarked = lines.findIndex((line) => line.marked);
  const start = Math.max(0, Math.min(lines.length - EXCERPT_LINES, (firstMarked < 0 ? 0 : firstMarked) - 1));
  return lines.slice(start, start + EXCERPT_LINES);
}

/**
 * `/calendar/room/:meetingId/review` — Beratung 13 (desktop) and 15 (tablet, compact): recording with
 * chapters, transcript, the summary draft with „Entwurf speichern" / „Für Mandant freigeben", the tasks of the
 * conversation with their visibility, and „Export" as PDF.
 */
export function ReviewPage() {
  const { meetingId = '' } = useParams();
  const review = useReview(meetingId);
  const compact = useMediaQuery('(max-width: 1199px)');
  const player = useRef<PlayerHandle>(null);
  const [time, setTime] = useState(0);

  if (review.isPending) {
    return (
      <div className={styles.page} aria-busy="true" aria-label="Wird geladen">
        <Skeleton width={90} height={8} />
        <div className={styles.skeletonTitle}>
          <Skeleton width={320} height={24} />
        </div>
        <div className={styles.grid}>
          <div className={styles.column}>
            <Skeleton variant="block" height={390} />
            <Skeleton variant="block" height={200} />
          </div>
          <div className={styles.column}>
            <Skeleton variant="block" height={300} />
            <Skeleton variant="block" height={220} />
          </div>
        </div>
      </div>
    );
  }

  if (review.isError) {
    return (
      <div className={styles.page}>
        <Banner
          tone="danger"
          actions={
            review.error instanceof ApiError && review.error.status === 404 ? (
              <Link to="/calendar">Zur Beratung</Link>
            ) : (
              <Button variant="secondary" size="small" onClick={() => void review.refetch()}>
                Erneut laden
              </Button>
            )
          }
        >
          {errorMessage(review.error)}
        </Banner>
      </div>
    );
  }

  const data = review.data;
  const notEnded = data.status !== 'ended' && data.status !== 'processed';
  const seek = (seconds: number) => player.current?.seek(seconds);
  const contact = data.header.contactName ?? 'Gast';

  return (
    <div className={clsx(styles.page, compact && styles.pageCompact)}>
      <div className={styles.header}>
        <div className={styles.headText}>
          <div className={styles.eyebrow}>Rückblick</div>
          <h1 className={styles.title}>
            {data.header.eventTypeName} · {compact ? shortPersonName(contact) : contact}
          </h1>
        </div>
        {compact ? (
          <span
            className={clsx(
              styles.statusPill,
              data.summary.status === 'approved' ? styles.statusApproved : styles.statusDraft,
            )}
          >
            {data.summary.status === 'approved'
              ? 'Zusammenfassung freigegeben'
              : 'Zusammenfassung im Entwurf'}
          </span>
        ) : (
          <span className={styles.headPill}>{data.header.pill}</span>
        )}
        <span className={styles.headActions}>
          <a className={styles.secondaryLink} href={exportHref(data.meetingId)} download>
            Export
          </a>
        </span>
      </div>

      {notEnded ? (
        <Banner
          tone="warning"
          className={styles.banner}
          actions={
            <Button
              variant="secondary"
              size="small"
              onClick={() => window.location.assign(`/calendar/room/${data.meetingId}`)}
            >
              Zum Meetingraum
            </Button>
          }
        >
          Der Rückblick steht erst nach dem Gespräch bereit.
        </Banner>
      ) : null}

      {compact ? (
        <div className={styles.compactLayout}>
          <Player ref={player} review={data} compact showChapters={false} onTime={setTime} />
          <details className={styles.summaryDetails}>
            <summary className={styles.summaryToggle}>Zusammenfassung</summary>
            <SummaryCard review={data} disabled={notEnded} embedded />
          </details>
          <div className={styles.compactGrid}>
            {data.chapters.length > 0 ? <ChaptersCard review={data} time={time} onSeek={seek} /> : null}
            <TasksCard review={data} compact disabled={notEnded} />
          </div>
          <TranscriptCard review={data} onSeek={seek} />
        </div>
      ) : (
        <div className={styles.grid}>
          <div className={styles.column}>
            <Player ref={player} review={data} />
            <TranscriptCard review={data} onSeek={seek} />
          </div>
          <div className={styles.column}>
            <SummaryCard review={data} disabled={notEnded} />
            <TasksCard review={data} disabled={notEnded} />
            <div className={styles.footnote}>
              Dieser Rückblick erscheint als Karte in der Kontakt- und in der Deal-Timeline. Die interne Notiz
              aus dem Gespräch liegt dort ebenfalls, aber nur für das Team.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Transcript lines; the times play the recording from there — without a recording they are plain text. */
function TranscriptLines({
  lines,
  onSeek,
}: {
  lines: TranscriptLine[];
  onSeek: ((seconds: number) => void) | null;
}) {
  return (
    <div className={styles.lines}>
      {lines.map((line, index) => (
        <div
          key={`${line.timeSeconds}-${index}`}
          className={clsx(styles.line, line.marked && styles.lineMarked)}
        >
          {onSeek ? (
            <button
              type="button"
              className={styles.lineTime}
              aria-label={`Aufzeichnung bei ${line.timeLabel} abspielen`}
              onClick={() => onSeek(line.timeSeconds)}
            >
              {line.timeLabel}
            </button>
          ) : (
            <span className={styles.lineTime}>{line.timeLabel}</span>
          )}
          <span className={styles.lineText}>
            {line.speakerName ? <span className={styles.speaker}>{line.speakerName} </span> : null}
            {line.text}
          </span>
        </div>
      ))}
    </div>
  );
}

function TranscriptCard({ review, onSeek }: { review: MeetingReview; onSeek: (seconds: number) => void }) {
  const [full, setFull] = useState(false);
  const { transcript } = review;
  const shown = excerpt(transcript.lines);
  const seek = review.recording ? onSeek : null;
  const waiting = transcript.status === 'pending' || transcript.status === 'processing';
  const marked = `${transcript.markedCount} ${transcript.markedCount === 1 ? 'markierte Stelle' : 'markierte Stellen'}`;
  return (
    <section className={styles.card} aria-labelledby="review-transcript">
      <div className={styles.cardHead}>
        <h2 id="review-transcript" className={styles.cardTitle}>
          Transkript
        </h2>
        <span className={styles.cardSub}>
          {transcript.statusText} · {marked}
        </span>
        {transcript.lines.length > 0 ? (
          <button type="button" className={styles.textLink} onClick={() => setFull(true)}>
            Volltext öffnen
          </button>
        ) : null}
      </div>
      {shown.length > 0 ? (
        <TranscriptLines lines={shown} onSeek={seek} />
      ) : (
        <div className={styles.emptyText}>
          {waiting ? 'Das Transkript erscheint hier, sobald es fertig ist.' : 'Transkript nicht verfügbar'}
        </div>
      )}
      <Dialog
        open={full}
        onClose={() => setFull(false)}
        title="Transkript"
        subtitle={`${transcript.statusText} · ${marked}`}
        width={720}
        footer={
          <Button variant="secondary" onClick={() => setFull(false)}>
            Schließen
          </Button>
        }
      >
        <TranscriptLines
          lines={transcript.lines}
          onSeek={
            seek
              ? (seconds) => {
                  setFull(false);
                  seek(seconds);
                }
              : null
          }
        />
      </Dialog>
    </section>
  );
}

function SummaryCard({
  review,
  disabled,
  embedded = false,
}: {
  review: MeetingReview;
  disabled: boolean;
  embedded?: boolean;
}) {
  const actions = useReviewActions(review.meetingId);
  const { toast } = useToast();
  const [text, setText] = useState(review.summary.text);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const approved = review.summary.status === 'approved';

  useEffect(() => {
    setText(review.summary.text);
  }, [review.summary.text]);

  async function save() {
    setError(null);
    try {
      await actions.save.mutateAsync(text);
      toast({ message: 'Entwurf gespeichert' });
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <section
      className={clsx(styles.summary, embedded && styles.summaryEmbedded)}
      aria-labelledby="review-summary"
    >
      <div className={styles.summaryHead}>
        <h2 id="review-summary" className={styles.cardTitle}>
          Zusammenfassung
        </h2>
        <span className={clsx(styles.tag, approved ? styles.tagApproved : styles.tagDraft)}>
          {approved ? 'Freigegeben' : 'Entwurf'}
        </span>
      </div>
      {approved ? (
        <>
          <div className={styles.hint}>
            Freigegeben
            {review.summary.approvedAt ? ` ${formatDateTime(review.summary.approvedAt)}` : ''}
            {review.summary.approvedByShort ? ` durch ${review.summary.approvedByShort}` : ''}
          </div>
          <div className={styles.summaryText}>{review.summary.text}</div>
        </>
      ) : (
        <>
          <div className={styles.hint}>
            {review.summary.generated ? 'Automatisch erzeugt, von Ihnen bearbeitbar. ' : ''}
            Der Mandant sieht sie erst nach Ihrer Freigabe.
          </div>
          <textarea
            className={styles.textarea}
            aria-label="Zusammenfassung"
            value={text}
            disabled={disabled}
            onChange={(event) => setText(event.target.value)}
          />
          {error ? (
            <div className={styles.error} role="alert">
              {error}
            </div>
          ) : null}
          <div className={styles.summaryActions}>
            {embedded ? null : (
              <button
                type="button"
                className={styles.primaryButton}
                disabled={disabled || !text.trim()}
                onClick={() => setConfirming(true)}
              >
                Für Mandant freigeben
              </button>
            )}
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={disabled || actions.save.isPending}
              aria-busy={actions.save.isPending || undefined}
              onClick={() => void save()}
            >
              Entwurf speichern
            </button>
          </div>
        </>
      )}
      <div className={styles.summaryNote}>
        {review.recordingEnabled
          ? 'Freigegeben wird nur die Zusammenfassung mit Aufgaben. Aufzeichnung und Transkript bleiben intern.'
          : 'Freigegeben wird nur die Zusammenfassung mit Aufgaben.'}
      </div>
      <ApproveDialog review={review} text={text} open={confirming} onClose={() => setConfirming(false)} />
    </section>
  );
}

function ApproveDialog({
  review,
  text,
  open,
  onClose,
}: {
  review: MeetingReview;
  text: string;
  open: boolean;
  onClose: () => void;
}) {
  const actions = useReviewActions(review.meetingId);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const visible = review.tasks.filter((task) => task.visibleToCustomer && !task.done).length;

  async function approve() {
    setError(null);
    try {
      await actions.approve.mutateAsync(text.trim());
      toast({ message: 'Zusammenfassung freigegeben' });
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Für Mandant freigeben?"
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button loading={actions.approve.isPending} onClick={() => void approve()}>
            Freigeben
          </Button>
        </>
      }
    >
      <p className={styles.dialogText}>
        Der Mandant erhält die Zusammenfassung
        {visible > 0
          ? ` mit ${visible} ${visible === 1 ? 'sichtbarer Aufgabe' : 'sichtbaren Aufgaben'}`
          : ''}{' '}
        per E-Mail. Danach lässt sie sich nicht mehr ändern.
        {review.recordingEnabled ? ' Aufzeichnung und Transkript bleiben intern.' : ''}
      </p>
      {error ? <Banner tone="danger">{error}</Banner> : null}
    </Dialog>
  );
}

function useTaskDone(meetingId: string) {
  const complete = useCompleteTask();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  async function done(task: MeetingTask) {
    setBusy(task.id);
    try {
      await complete.mutateAsync({ id: task.id, body: {} });
      await queryClient.invalidateQueries({ queryKey: reviewKey(meetingId) });
    } catch (failure) {
      toast({ message: errorMessage(failure) });
    } finally {
      setBusy(null);
    }
  }
  return { done, busy };
}

function TasksCard({
  review,
  compact = false,
  disabled,
}: {
  review: MeetingReview;
  compact?: boolean;
  disabled: boolean;
}) {
  const actions = useReviewActions(review.meetingId);
  const { toast } = useToast();
  const { done, busy } = useTaskDone(review.meetingId);
  const [confirming, setConfirming] = useState(false);
  const approved = review.summary.status === 'approved';

  async function toggleVisibility(task: MeetingTask) {
    try {
      await actions.visibility.mutateAsync({ taskId: task.id, visibleToCustomer: !task.visibleToCustomer });
    } catch (failure) {
      toast({ message: errorMessage(failure) });
    }
  }

  return (
    <section className={clsx(styles.card, compact && styles.cardCompact)} aria-labelledby="review-tasks">
      <h2 id="review-tasks" className={clsx(styles.cardTitle, compact && styles.cardTitleCompact)}>
        {compact ? 'Aufgaben' : 'Aufgaben aus dem Gespräch'}
      </h2>
      {review.tasks.length === 0 ? (
        <div className={styles.emptyText}>Keine Aufgaben aus dem Gespräch.</div>
      ) : (
        <div className={clsx(styles.tasks, compact && styles.tasksCompact)}>
          {review.tasks.map((task) => {
            const meta = [task.assigneeShort, task.dueLabel, task.sourceLabel].filter(Boolean).join(' · ');
            return (
              <div
                key={task.id}
                className={clsx(
                  compact ? styles.taskCompact : styles.task,
                  !compact && task.visibleToCustomer && styles.taskVisible,
                  task.done && styles.taskDone,
                )}
              >
                <label className={styles.taskCheck}>
                  <input
                    type="checkbox"
                    className={styles.srOnly}
                    checked={task.done}
                    disabled={task.done || busy !== null}
                    onChange={() => void done(task)}
                    aria-label={`${task.title} erledigt`}
                  />
                  <span
                    className={clsx(styles.box, compact && styles.boxCompact, task.done && styles.boxOn)}
                    aria-hidden="true"
                  >
                    {task.done ? <Check size={10} strokeWidth={2.5} /> : null}
                  </span>
                </label>
                {compact ? (
                  <span className={styles.taskCompactText}>
                    {task.title}
                    {task.dueLabel ? ` · ${task.dueLabel}` : ''}
                  </span>
                ) : (
                  <>
                    <span className={styles.taskText}>
                      <span className={styles.taskTitle}>{task.title}</span>
                      <span className={styles.taskMeta}>{meta}</span>
                    </span>
                    <button
                      type="button"
                      className={clsx(styles.visibility, task.visibleToCustomer && styles.visibilityOn)}
                      aria-pressed={task.visibleToCustomer}
                      aria-label={`${task.title}: ${task.visibleToCustomer ? 'Sichtbar' : 'Intern'}`}
                      disabled={approved || actions.visibility.isPending}
                      onClick={() => void toggleVisibility(task)}
                    >
                      {task.visibleToCustomer ? 'Sichtbar' : 'Intern'}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {compact && !approved ? (
        <>
          <Button
            block
            size="small"
            className={styles.approveBlock}
            disabled={disabled || !review.summary.text.trim()}
            onClick={() => setConfirming(true)}
          >
            Für Mandant freigeben
          </Button>
          <ApproveDialog
            review={review}
            text={review.summary.text}
            open={confirming}
            onClose={() => setConfirming(false)}
          />
        </>
      ) : null}
    </section>
  );
}

function ChaptersCard({
  review,
  time,
  onSeek,
}: {
  review: MeetingReview;
  time: number;
  onSeek: (seconds: number) => void;
}) {
  const active = [...review.chapters].reverse().find((chapter) => chapter.timeSeconds <= time) ?? null;
  return (
    <section className={clsx(styles.card, styles.cardCompact)} aria-labelledby="review-chapters">
      <h2 id="review-chapters" className={clsx(styles.cardTitle, styles.cardTitleCompact)}>
        Kapitel
      </h2>
      <div className={styles.chapterList}>
        {review.chapters.map((chapter) => (
          <button
            key={chapter.timeSeconds}
            type="button"
            className={clsx(styles.chapterRow, active === chapter && styles.chapterRowActive)}
            aria-current={active === chapter ? 'true' : undefined}
            onClick={() => onSeek(chapter.timeSeconds)}
          >
            <span className={styles.chapterTime}>{chapter.timeLabel}</span>
            {chapter.label}
          </button>
        ))}
      </div>
    </section>
  );
}
